"use client";
import { GithubIcon, TwitterIcon } from "lucide-react";
import Link from "next/link";

import { comparisons } from "@/lib/comparisons";
import { useAppConfig } from "@/lib/config";
import Discord from "@/lib/icons/Discord";
import Logo from "@/lib/icons/Logo";

export default function Footer() {
	const config = useAppConfig();

	const columns: {
		heading: string;
		links: { label: string; href: string; external?: boolean }[];
	}[] = [
		{
			heading: "Product",
			links: [
				{ label: "Features", href: "/features" },
				{ label: "Pricing", href: "/#pricing" },
				{ label: "Changelog", href: "/changelog" },
				{ label: "Blog", href: "/blog" },
				{ label: "Self-hosting", href: "/features/self-hosting" },
			],
		},
		{
			heading: "Resources",
			links: [
				{ label: "Docs", href: config.docsUrl ?? "", external: true },
				{
					label: "Self-host guide",
					href: `${config.docsUrl ?? ""}/self-host`,
					external: true,
				},
				{ label: "GitHub", href: config.githubUrl ?? "", external: true },
				{ label: "Contact Us", href: "mailto:contact@meetploy.com" },
			],
		},
		{
			heading: "Legal",
			links: [
				{ label: "Overview", href: "/legal" },
				{ label: "Terms", href: "/legal/terms" },
				{ label: "Privacy", href: "/legal/privacy" },
				{ label: "Sub-processors", href: "/legal/sub-processors" },
			],
		},
		{
			heading: "Community",
			links: [
				{ label: "Twitter", href: config.twitterUrl, external: true },
				{ label: "Discord", href: config.discordUrl, external: true },
			],
		},
		{
			heading: "Compare",
			links: [
				{ label: "All comparisons", href: "/compare" },
				...comparisons.map((comparison) => ({
					label: `Ploy vs ${comparison.competitor}`,
					href: `/compare/${comparison.slug}`,
				})),
			],
		},
	];

	return (
		<footer className="border-t border-border py-12">
			<div className="container mx-auto px-4">
				<div className="flex flex-col md:flex-row md:items-start md:justify-between">
					<div className="mb-6 md:mb-0">
						<div className="flex items-center space-x-2">
							<Logo className="h-8 w-8 rounded-none" />
							<span className="text-lg font-bold tracking-tight">Ploy</span>
						</div>
						<p className="mt-2 max-w-xs text-sm text-muted-foreground">
							The open serverless cloud for apps and AI agents.
						</p>
						<p className="mt-2 text-sm text-muted-foreground">
							© {new Date().getFullYear()} Ploy. All rights reserved.
						</p>
						<div className="mt-4 flex items-center space-x-4">
							<a
								href={config.githubUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="text-muted-foreground transition-colors hover:text-foreground"
								aria-label="GitHub"
							>
								<GithubIcon className="h-5 w-5" />
							</a>
							<a
								href={config.twitterUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="text-muted-foreground transition-colors hover:text-foreground"
								aria-label="Twitter"
							>
								<TwitterIcon className="h-5 w-5" />
							</a>
							<a
								href={config.discordUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="text-muted-foreground transition-colors hover:text-foreground"
								aria-label="Discord"
							>
								<Discord className="h-5 w-5" />
							</a>
						</div>
					</div>

					<div className="grid w-full grid-cols-2 gap-8 md:w-auto md:grid-cols-5">
						{columns.map((column) => (
							<div key={column.heading}>
								<h3 className="mb-3 text-xs font-semibold uppercase tracking-widest">
									{column.heading}
								</h3>
								<ul className="space-y-2">
									{column.links.map((link) => (
										<li key={link.label}>
											{link.external || link.href.startsWith("mailto:") ? (
												<a
													href={link.href}
													target={link.external ? "_blank" : undefined}
													rel={
														link.external ? "noopener noreferrer" : undefined
													}
													className="text-sm text-muted-foreground hover:text-foreground"
												>
													{link.label}
												</a>
											) : (
												<Link
													href={link.href}
													prefetch={true}
													className="text-sm text-muted-foreground hover:text-foreground"
												>
													{link.label}
												</Link>
											)}
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				</div>
			</div>
		</footer>
	);
}
